import React, { useCallback /* , useEffect */ } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useDropzone,
  FileRejection,
  DropEvent,
  FileError,
} from 'react-dropzone';
import {
  uploadFiles,
  cancelFileUpload,
  uploadAttachment,
  cancelAttachmentUpload,
} from '../../redux/uploads/actions';
import {
  getUploadsByTag,
  getAttachmentUploadsByTag,
} from '../../redux/uploads/selectors';
import type {
  UploadSuccessCallback,
  UploadFailureCallback,
  UploadAttachmentSuccessCallback,
  AttachmentType,
} from '../../redux/uploads/types';
import { UploadType } from '../../redux/uploads/types';
import { useVerification } from '../../VerificationPopup/withVerification';
import { MIME_CONFIG, mimeTypesToDropzoneAccept } from '../mime';

export interface FileUploadOptions {
  /** use to keep track of uploads belonging to this particular component */
  id?: string;
  /** used to determine which api endpoint we'll be calling */
  uploadType: UploadType;
  uploadParams?: {
    /** attachments have different types (pcp, exclusive, etc.) */
    attachmentType?: AttachmentType;
    /** if submitting an attachment we have to specify the parent deviation's id */
    parentDeviationId?: number;
    /** in case of an edit, deviation id we're replacing the image to. */
    deviationId?: number | React.RefObject<number | undefined>;
    /** in case of an originals delivery, the deviation and order/response ids */
    originalsDeviationId?: number;
    originalsResponseId?: number;
    /** folder id and name are used to determine where in stash will new deviation uploads be placed */
    folderId?: number;
    folderName?: string;
    /** if true, backend will apply preset values to the new deviation */
    useDefaults?: boolean;
  };

  acceptedMimeTypes?: string[];
  allowMultiple?: boolean;
  allowDragAndDrop?: boolean;
  allowClickOnRoot?: boolean;

  onDrop?: (files: File[], ev: DropEvent) => boolean | Promise<boolean>;
  /** https://react-dropzone.js.org/#src */
  validator?: (file: File) => FileError | FileError[] | null;
  /** https://react-dropzone.js.org/#src */
  onDropRejected?: (files: FileRejection[]) => void;
  onSuccess?: UploadSuccessCallback | UploadAttachmentSuccessCallback;
  onFailure?: UploadFailureCallback;
}

/**
 *  Basic file uploader to stash.
 *  Usage: add the input returned somewhere to the markup, and call showFilePicker to open the file picker programmatically.
 *  To create a drag-and-drop area, spread the props from getDropzoneRootProps into your element
 */
export function useFileUpload({
  id,
  uploadType,
  uploadParams = {},
  acceptedMimeTypes = MIME_CONFIG.forDeviationSubmit,
  allowMultiple = true,
  allowDragAndDrop = true,
  allowClickOnRoot = true,
  onDrop,
  validator,
  onDropRejected: onDropRejectedProp,
  onSuccess,
  onFailure,
}: FileUploadOptions) {
  const {
    parentDeviationId,
    deviationId,
    folderId,
    folderName,
    originalsDeviationId,
    originalsResponseId,
    useDefaults = false,
    attachmentType = 'pcp',
  } = uploadParams || {};
  const dispatch = useDispatch();
  const { isVerificationNeeded, pushVerificationPopup } = useVerification();
  const activeUploads = useSelector(state =>
    uploadType === UploadType.ATTACHMENT
      ? getAttachmentUploadsByTag(state, id)
      : getUploadsByTag(state, id)
  );

  const onFilePicked = useCallback(
    async (files: File[], ev: DropEvent) => {
      if (isVerificationNeeded) {
        pushVerificationPopup();
        return;
      }

      if (!files || files.length < 1) {
        return;
      }

      if (onDrop && !(await onDrop(files, ev))) {
        return;
      }

      if (uploadType === UploadType.ATTACHMENT) {
        files.reverse().forEach((file, idx) => {
          dispatch(
            uploadAttachment({
              tag: id,
              file,
              parentDeviationId: parentDeviationId!,
              type: attachmentType,
              onSuccess,
              onFailure,
            })
          );
        });
        return;
      }

      dispatch(
        uploadFiles(
          files.reverse().map((file, idx) => ({
            tag: id,
            file,
            uploadType,
            deviationId:
              !deviationId ||
              typeof deviationId === 'string' ||
              typeof deviationId === 'number'
                ? deviationId
                : deviationId?.current,
            folderId,
            folderName,
            originalsDeviationId,
            originalsResponseId,
            useDefaults,
            onSuccess,
            onFailure,
          }))
        )
      );
    },
    [
      isVerificationNeeded,
      onDrop,
      uploadType,
      dispatch,
      pushVerificationPopup,
      id,
      parentDeviationId,
      deviationId,
      folderId,
      folderName,
      originalsDeviationId,
      originalsResponseId,
      attachmentType,
      useDefaults,
      onSuccess,
      onFailure,
    ]
  );

  const onDropRejected = useCallback(
    // wrap so we don't need the onDropRejected prop for this component
    // to include the event
    (files: FileRejection[], dropevent: DropEvent) =>
      onDropRejectedProp?.(files),
    [onDropRejectedProp]
  );

  const cancelUpload = useCallback(
    uploadId =>
      dispatch(
        uploadType === UploadType.ATTACHMENT
          ? cancelAttachmentUpload({ uploadId, type: attachmentType })
          : cancelFileUpload({ uploadId })
      ),
    [dispatch, uploadType, attachmentType]
  );

  const acceptAll =
    acceptedMimeTypes.length === 1 && acceptedMimeTypes[0] === '*';

  const {
    getRootProps,
    getInputProps,
    open,
    isDragActive,
    isDragReject,
    isDragAccept,
  } = useDropzone({
    validator,
    onDropAccepted: onFilePicked,
    onDropRejected,
    accept: acceptAll
      ? undefined
      : mimeTypesToDropzoneAccept(acceptedMimeTypes),
    multiple: allowMultiple,
    noDrag: !allowDragAndDrop,
    noClick: !allowClickOnRoot,
  });

  const input = <input {...getInputProps()} />;

  return {
    showFilePicker: open,
    input,
    activeUploads,
    cancelUpload,
    getDropzoneRootProps: getRootProps,
    isDragging: isDragActive,
    isDragAccept,
    isDragReject,
  };
}
